import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Stack from '@mui/material/Stack';
import MenuFoldOutlined from '@ant-design/icons/MenuFoldOutlined';
import MenuUnfoldOutlined from '@ant-design/icons/MenuUnfoldOutlined';

import {
	MenuOrientation,
	ThemeMode,
	DRAWER_WIDTH,
	MINI_DRAWER_WIDTH,
} from 'src/config';

import FullScreen from 'mantis/layout/Dashboard/Header/HeaderContent/FullScreen';

import useInterface from 'src/hooks/useInterface';
import useConfig from 'src/hooks/useConfig';
import IconButton from 'src/components/ui/IconButton';
import NavUser from 'src/components/ui/NavUser';
import UserBadge from 'src/components/ui/UserBadge';
import Customization from 'src/components/ui/Customization';

import AppBarStyled from './styled/AppBarStyled';
import { CALL_STICKER_HEIGHT } from '../CallSticky/constants';

const Header = (props) => {
	const {
		user,
		drawerOpen: drawerOpenProp,
		onClickDrawer,
		enableFullscreenBtn = true,
		enableCustomizationBtn = true,
	} = props;

	const { isDrawerOpen, onToggleDrawer, isStickCall } = useInterface();
	const drawerOpen =
		drawerOpenProp === undefined ? isDrawerOpen : drawerOpenProp;

	const theme = useTheme();
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	const { mode, menuOrientation } = useConfig();

	const isHorizontal =
		menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

	const iconBackColor =
		mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

	const handleDrawer = useCallback(() => {
		if (onClickDrawer) {
			onClickDrawer();
		} else {
			onToggleDrawer();
		}
	}, [onClickDrawer, onToggleDrawer]);

	const mainHeader = (
		<Toolbar>
			{!isHorizontal ? (
				<IconButton
					aria-label="open drawer"
					onClick={handleDrawer}
					edge="start"
					color="secondary"
					variant="light"
					sx={{
						color: 'text.primary',
						bgcolor: drawerOpen ? 'transparent' : iconBackColor,
						ml: { xs: 0, lg: -2 },
					}}
				>
					{!drawerOpen ? (
						<MenuUnfoldOutlined />
					) : (
						<MenuFoldOutlined />
					)}
				</IconButton>
			) : null}
			<Stack alignItems="center" spacing={0.5} direction="row" ml="auto">
				{enableFullscreenBtn ?? <FullScreen />}
				{enableCustomizationBtn ?? <Customization />}
				{user && <UserBadge {...user} />}
			</Stack>
		</Toolbar>
	);

	// app-bar params
	const appBar = {
		position: 'fixed',
		color: 'inherit',
		elevation: 0,
		sx: {
			top: isStickCall ? CALL_STICKER_HEIGHT : 0,
			transition: '.2s top',
			borderBottom: '1px solid',
			borderBottomColor: 'divider',
			zIndex: 1200,
			width: isHorizontal
				? '100%'
				: {
					xs: '100%',
					lg: drawerOpen
						? `calc(100% - ${DRAWER_WIDTH}px)`
						: `calc(100% - ${MINI_DRAWER_WIDTH}px)`,
				},
		},
	};

	return (
		<>
			{!downLG ? (
				<AppBarStyled open={drawerOpen} {...appBar}>
					{mainHeader}
				</AppBarStyled>
			) : (
				<AppBar {...appBar}>{mainHeader}</AppBar>
			)}
		</>
	);
};

Header.propTypes = {
	drawerOpen: PropTypes.bool,
	user: PropTypes.shape({ ...NavUser.propTypes }),
	HeaderContentRP: PropTypes.elementType,
	onClickDrawer: PropTypes.func,
	enableFullscreenBtn: PropTypes.bool,
	enableCustomizationBtn: PropTypes.bool,
};

export default Header;
